import { useQuery } from "@tanstack/react-query";
import { useUIContext } from "@/contexts/UIContext";

/**
 * The counts the menu hangs on its rows (D10; Andrew, 2026-10-26): the
 * reader's outstanding tasks, the project's offers in play and its sales, and
 * the packages waiting in the Signing Queue. Nothing on Showroom or the Agent
 * Portal. One cheap request per project
 * (server/routes/navCounts.ts), shared by the sidebar, the phone tab bar and
 * its More sheet through one query key.
 *
 * Keyed by nav item, so a row asks `counts[item.key]`. Only in a project —
 * nothing outside one has a count. Never throws: a failed count is no badge,
 * not an error on every screen.
 */
/** `waiting`: someone has to act (a task, a package to sign), so the phone's
 *  More button adds it up; a total (offers, sales) is only how many there are. */
export type NavCounts = Partial<Record<string, { count: number; noun: string; waiting?: boolean }>>;

export function navCountsKey(projectId: string) {
  return [`/api/projects/${projectId}/nav-counts`] as const;
}

export function useNavCounts(): NavCounts {
  const { mode, projectId } = useUIContext();
  const enabled = mode === "project" && !!projectId;
  const { data } = useQuery<{ overdueTasks: number; openTasks?: number; pendingWorksheets: number; totalOffers?: number; totalSales?: number; signingOutstanding?: number }>({
    queryKey: navCountsKey(projectId ?? ""),
    enabled,
    staleTime: 60_000,
    // A count someone is waiting on should move without a reload, but slowly:
    // it is a hint, and this poll must not be mistaken for activity (it is
    // not on the session-renewal allow-list — see CLAUDE.md, Session timeouts).
    refetchInterval: 3 * 60_000,
    refetchIntervalInBackground: false,
    retry: false,
  });
  if (!enabled || !data) return {};
  return {
    // Outstanding, not just overdue (Andrew, 2026-10-10). A server from
    // before openTasks existed still answers with the overdue count.
    tasks: data.openTasks !== undefined ? { count: data.openTasks, noun: "open task", waiting: true } : { count: data.overdueTasks, noun: "overdue task", waiting: true },
    offers: { count: data.totalOffers ?? data.pendingWorksheets, noun: "offer" },
    sales: { count: data.totalSales ?? 0, noun: "sale" },
    salesList: { count: data.totalSales ?? 0, noun: "sale" },
    signingQueue: { count: data.signingOutstanding ?? 0, noun: "unsigned package", waiting: true },
  };
}
